import type { ClientPlatform } from '@/shared/lib/device';
import {
  androidStoreUrl,
  GENERIC_FIXE_APP_ANDROID_URL,
  GENERIC_FIXE_APP_IOS_PASSBOOK_IDS,
  GENERIC_FIXE_APP_IOS_URL,
  WALLET_FLAVORS,
  WALLET_UNION_PASSBOOK_IDS,
  walletUnionUrl,
} from './wallet-flavors';

/**
 * Como o cartão chega ao celular de quem já tem cadastro — usado pela tela de
 * sucesso do convite e pela aba "Meu cartão" da área do participante.
 *
 * - `handoff`: computador → QR para continuar no celular.
 * - `app`: loja com app próprio, Wallet Union ou app genérico Fixe.
 * - `apple`: `.pkpass` direto na Carteira do iPhone.
 * - `google`: link "Add to Google Wallet".
 */
export type CardDelivery =
  | { kind: 'handoff' }
  | { kind: 'app'; href: string; label: string }
  | { kind: 'apple' }
  | { kind: 'google' };

interface CardDeliveryPassbook {
  idPassbook: string;
  organizationName: string;
  hasFlavor?: string;
  hasGWallet?: boolean;
}

/**
 * Ordem exata do legado (legacy/fixe-link-2 routes/invite.js): Wallet Union >
 * `hasGWallet` > `hasFlavor` > app genérico. Cartão-presente (`giftMode`)
 * sempre cai em Wallet Union no Android quando não há `hasGWallet` — nunca
 * olha `hasFlavor`/app genérico (generateInviteGift).
 */
export function resolveCardDelivery(
  passbook: CardDeliveryPassbook,
  platform: ClientPlatform,
  serialNumber: string,
  giftMode = false,
): CardDelivery {
  if (platform === 'desktop') return { kind: 'handoff' };

  const { idPassbook, hasFlavor, hasGWallet, organizationName } = passbook;
  const flavor = hasFlavor ? WALLET_FLAVORS[hasFlavor] : undefined;
  const isForcedWalletUnion = !giftMode && WALLET_UNION_PASSBOOK_IDS.has(idPassbook);

  if (platform === 'ios') {
    if (!giftMode && flavor) return { kind: 'app', href: flavor.iosUrl, label: `Baixar app ${organizationName}` };
    if (!giftMode && GENERIC_FIXE_APP_IOS_PASSBOOK_IDS.has(idPassbook)) {
      return { kind: 'app', href: GENERIC_FIXE_APP_IOS_URL, label: 'Baixar app Fixe CRM' };
    }
    return { kind: 'apple' };
  }

  // Android: hasGWallet tem prioridade sobre Wallet Union nos dois fluxos.
  if (!isForcedWalletUnion && hasGWallet) return { kind: 'google' };
  if (giftMode || isForcedWalletUnion) {
    return { kind: 'app', href: walletUnionUrl(idPassbook, serialNumber), label: 'Baixar Wallet Union' };
  }
  if (flavor) {
    return { kind: 'app', href: androidStoreUrl(flavor.androidPackageId), label: `Baixar app ${organizationName}` };
  }
  return { kind: 'app', href: GENERIC_FIXE_APP_ANDROID_URL, label: 'Baixar app Fixe CRM' };
}

/** "Já adicionou?" no iPhone — abre o app Carteira. */
export const APPLE_WALLET_APP_URL = 'shoebox://';

/** Abre o Google Wallet direto (intent://), caindo pra Play Store se o app não estiver instalado — legacy/fixe-link-2 views/v2/partials/open_wallet_cta.ejs. */
export function openGoogleWalletApp(): void {
  const fallback = 'https://play.google.com/store/apps/details?id=com.google.android.apps.walletnfcrel';
  window.location.href = `intent://wallet.google.com/#Intent;scheme=https;package=com.google.android.apps.walletnfcrel;S.browser_fallback_url=${encodeURIComponent(fallback)};end`;
}
